import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import biClient from "@apis/businessIntelligenceClient";
import type { DashboardMenuItem } from "@apis/businessIntelligenceClient/businessIntelligenceClient.dto";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@hooks/useFeatures";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@hooks/useLanguage";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasPermission } from "@hooks/usePermissions";
import type { RootState } from "@/types/RootState";
import type { MenuRoute } from "./menuTypes";

/**
 * The state the Dashboards group is registered under, in every menu that
 * carries it. The hook finds the group by this value rather than by position.
 */
export const DASHBOARD_GROUP_STATE = "openDashboards";

/**
 * Menu route with the fields the dashboard entries add on top of MenuRoute.
 */
export interface DashboardMenuRoute extends MenuRoute {
  category?: string;
  type?: string;
  reportType?: string;
}

/**
 * Fields the menu endpoint returns that aren't in the base DTO.
 */
interface ExtendedDashboardMenuItem extends DashboardMenuItem {
  menuName?: string;
  powerBiReportType?: string;
}

export interface DashboardMenuItemsOptions {
  /**
   * The menu does not carry the Dashboards group at all — skip the fetch.
   * `onApplied` still fires, so a caller gating its `loading` on it is not
   * left waiting for a request that will never happen.
   */
  skip: boolean;
  /**
   * Whether the entries carry `category: "dashboard"`, which
   * `NavList.buildMenuItemHref` reads to append the stored `reportOrgId` to the
   * link. True for HOME, false for OWN — at own level the absence of `?org=` is
   * the level. The own-level `/bi_dashboards` entry omits it for the same reason.
   */
  tagWithDashboardCategory: boolean;
  /**
   * Runs once the views have been applied (or skipped). Used by the menus to
   * flip their first-init gate and notify their consumers.
   */
  onApplied?: () => void;
}

/**
 * Fetches the favourited BI dashboards and injects them into the group
 * registered under {@link DASHBOARD_GROUP_STATE}, followed by "All dashboards".
 *
 * Shared by the Home menu and the own-level menu (STIC-16186 R1): a login with
 * a Home menu gets the group there, one without it — SYSTEM always, a partner
 * without `legacy_partner_home_menu` — gets the same group on the own level.
 * The two never show it at once, so the same fetch serves whichever asks.
 *
 * "Favourited" is decided server-side: `/rest/bi/dashboard/menu` returns only
 * dashboards that are `favouriteForAll` or favourited by the calling user
 * (`BusinessIntelligenceResourceImpl.getMenuDashboards`).
 *
 * @param setRoutes - state setter for the menu's routes array
 * @param options - see {@link DashboardMenuItemsOptions}
 */
const useDashboardMenuItems = <T extends MenuRoute>(
  setRoutes: React.Dispatch<React.SetStateAction<T[]>>,
  { skip, tagWithDashboardCategory, onApplied }: DashboardMenuItemsOptions
): void => {
  const global = useSelector((state: RootState) => state.all);

  const applyDashboardViews = useCallback(
    (views: DashboardMenuRoute[]) => {
      // "All dashboards" is always last, under the favourited ones.
      views.push({
        view: hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_HOME_DASHBOARD),
        to: "/all_dashboards",
        layout: "",
        type: "bi",
        name: translate(LanguageKey.all_dashboards_key),
        ...(tagWithDashboardCategory ? { category: "dashboard" } : {}),
      });
      setRoutes((oldRoutes) => {
        const route = oldRoutes.find((x) => x.state === DASHBOARD_GROUP_STATE);
        if (route) {
          route.views = [...views];
        }
        return [...oldRoutes];
      });
      onApplied?.();
    },
    [setRoutes, tagWithDashboardCategory, onApplied]
  );

  useEffect(() => {
    if (skip) {
      // Clear the group rather than just leaving it: `useAvailibleMenu` merges a
      // section's children into `allPaths` even when the section is hidden, so
      // entries surviving a flip to `skip` (the own level dropping to the cross
      // level, say) would keep claiming /all_dashboards for a menu that no
      // longer offers it.
      setRoutes((oldRoutes) => {
        const route = oldRoutes.find((x) => x.state === DASHBOARD_GROUP_STATE);
        if (route?.views?.length) {
          route.views = [];
          return [...oldRoutes];
        }
        return oldRoutes;
      });
      onApplied?.();
      return;
    }

    const dashboardViews: DashboardMenuRoute[] = [
      {
        view: hasFeature(FeatureKey.utilization_suez),
        to: "/suez_dashboard",
        layout: "",
        name: translate(LanguageKey.suez_dashboard_key),
        ...(tagWithDashboardCategory ? { category: "dashboard" } : {}),
      },
    ];

    if (hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.DASHBOARD_VIEW)) {
      biClient
        .getDashBoardsForMenu({})
        .then((response) => {
          const dashboards = response.data as ExtendedDashboardMenuItem[];

          for (let i = 0; i < dashboards.length; i++) {
            dashboardViews.push({
              view:
                hasFeature(FeatureKey.dashboard) &&
                hasPermission(UserPermission.MENU_HOME_DASHBOARD),
              to: "/bi_dashboard/" + dashboards[i].id,
              layout: "",
              type: "bi",
              name: dashboards[i].menuName || dashboards[i].name,
              reportType: dashboards[i].powerBiReportType,
              ...(tagWithDashboardCategory ? { category: "dashboard" } : {}),
            });
          }
          applyDashboardViews(dashboardViews);
        })
        .catch(() => {
          applyDashboardViews(dashboardViews);
        });
    } else {
      applyDashboardViews(dashboardViews);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, global.reloadSidebar]);
};

export default useDashboardMenuItems;
